import React, { useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import PropTypes from 'prop-types';
import {
  Banknote,
  CircleCheckBig,
  ChevronDown,
  Coins,
  Download,
  FolderDown,
  Hospital,
  Info,
  StickyNote,
  UserRound,
  UserRoundPen,
} from 'lucide-react';
import { useGetPilgrim, useGetUser } from 'hooks';
import { formatNumber, getUserName } from 'helpers';
import CategoryBadge from 'components/shared/CategoryBadge';
import { LockableButton } from 'components/shared/Lockable';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from 'components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import MedicalVisitHistory from './MedicalVisitHistory';
import PilgrimDepositItem, { formatPilgrimDate } from './PilgrimDepositItem';

const sexLabels = { F: 'Féminin', M: 'Masculin', I: 'Autre' };

const DetailField = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className={`mt-1 break-words text-sm ${value ? 'text-gray-900' : 'text-gray-400'}`}>
      {value || 'Non renseigné'}
    </dd>
  </div>
);

DetailField.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

const SectionHeading = ({ icon: SectionIcon, children }) => (
  <div className="mb-3 flex items-center gap-3">
    <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
      <SectionIcon className="size-4 text-hajj-primary" /> {children}
    </h2>
  </div>
);

SectionHeading.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const formatMoney = (value) => `${formatNumber(value)} F CFA`;

const PilgrimDetails = ({
  pilgrimData = null,
  isReadOnly = false,
  onOpenEditModal = () => {},
  onOpenInvoiceModal = () => {},
  onOpenDeleteModal = () => {},
  handleGenerateMedicalCertificate = () => {},
  onOpenAutoFocus = (event) => event.preventDefault(),
  onCloseAutoFocus = (event) => event.preventDefault(),
}) => {
  const { isAgency, isAGLO } = useGetUser();
  const {
    id,
    pilgrim,
    depositTransferred,
    cartTotal,
    deposits,
    agencyName,
    note,
    firstname,
    lastname,
    npi,
    nic,
    isInternational,
    passport,
    passportExpirationDate,
    birthdate,
    categoryName,
    isHealthy,
    initials,
    selfRegistered,
    isGrouped,
    groupName,
    isNpiValid,
    isInvoiceFullyPaid,
    isApprovedForMAECStep,
    hasCompletedMedicalCheckup,
    isAssignFromPreviousCampaign,
    department,
    health,
  } = useGetPilgrim(pilgrimData);

  const depositAllowed = isNpiValid && !selfRegistered && (!isInternational || !!nic);
  const amountDue = Number(cartTotal) || 0;
  const amountPaid = Number(depositTransferred) || 0;
  const amountRemaining = Math.max(0, amountDue - amountPaid);
  const depositHistory = useMemo(
    () =>
      [...(Array.isArray(deposits) ? deposits : [])].sort((a, b) => (b.created || '').localeCompare(a.created || '')),
    [deposits]
  );
  const medicalHistory = useMemo(() => {
    if (!isAGLO || !health?.notes) return [];

    try {
      const decodedNotes = jwtDecode(health.notes);
      return Array.isArray(decodedNotes) ? decodedNotes : [decodedNotes];
    } catch {
      return [];
    }
  }, [health?.notes, isAGLO]);
  const birthdateLabel = formatPilgrimDate(birthdate);
  const passportExpirationLabel = formatPilgrimDate(passportExpirationDate);
  const sexLabel = sexLabels[pilgrim?.sex] || pilgrim?.sex || null;
  const completedConditions = [isInvoiceFullyPaid, hasCompletedMedicalCheckup, isApprovedForMAECStep].filter(
    Boolean
  ).length;

  return (
    <SheetContent
      side="right"
      className="w-full gap-0 overflow-hidden p-0 font-[Inter,sans-serif] sm:max-w-[480px] 2xl:max-w-[500px]"
      onOpenAutoFocus={onOpenAutoFocus}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      <SheetHeader className="shrink-0 border-b px-5 pb-4 pt-3 text-left">
        <SheetTitle className="pr-10 text-sm text-gray-600">Détails du pèlerin</SheetTitle>
        <SheetDescription className="sr-only">
          Identité, statuts, situation financière et actions disponibles pour ce pèlerin.
        </SheetDescription>
        <div className="flex min-w-0 items-start gap-3 pt-2">
          <Avatar className="size-11 shrink-0">
            <AvatarFallback className="bg-hajj-primary text-sm font-semibold text-white">
              {initials || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="break-words text-lg font-semibold leading-6 text-gray-900">
              {getUserName(firstname, lastname)}
            </p>
            {categoryName && <CategoryBadge categoryName={categoryName} className="mt-1" />}
          </div>
        </div>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-5">
        {isAssignFromPreviousCampaign && (
          <div className="mb-5 flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
            <Info className="mt-0.5 size-4 shrink-0" /> Transféré de la campagne précédente
          </div>
        )}

        <section className="border-b pb-6" aria-labelledby="conditions-title">
          <div className="mb-3 flex items-center justify-between gap-3">
            <SectionHeading icon={CircleCheckBig}>
              <span id="conditions-title">Conditions du dossier</span>
            </SectionHeading>
            <span className="mb-3 shrink-0 text-xs font-medium text-gray-500">{completedConditions} / 3 remplies</span>
          </div>
          <div className="space-y-2">
            <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
              <p className="text-sm font-medium text-gray-800">Paiement intégral</p>
              <Badge variant={isInvoiceFullyPaid ? 'success' : 'pending'}>
                {isInvoiceFullyPaid ? 'Payé' : 'En attente'}
              </Badge>
            </div>
            <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
              <p className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Hospital className="size-4 text-hajj-primary" /> Visite médicale
              </p>
              {isHealthy === null ? (
                <Badge variant="pending">En attente</Badge>
              ) : isReadOnly ? (
                <Badge variant={isHealthy ? 'success' : 'destructive'}>{isHealthy ? 'Apte' : 'Inapte'}</Badge>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant={isHealthy ? 'ghost' : 'destructive'}
                      size="sm"
                      className={`h-7 gap-1 px-2 text-xs ${isHealthy ? 'text-emerald-700 hover:bg-emerald-50' : ''}`}
                      onClick={() => handleGenerateMedicalCertificate(pilgrimData)}
                      aria-label={`${isHealthy ? 'Apte' : 'Inapte'} — télécharger le certificat médical`}
                    >
                      <Download className="size-3.5" /> {isHealthy ? 'Apte' : 'Inapte'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Télécharger votre certificat médical</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
              <p className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <FolderDown className="size-4 text-hajj-primary" /> Passeport déposé
              </p>
              <Badge variant={isApprovedForMAECStep ? 'success' : 'pending'}>
                {isApprovedForMAECStep ? 'Déposé' : 'En attente'}
              </Badge>
            </div>
          </div>
        </section>

        <section className="mt-6 border-b pb-6" aria-labelledby="identity-title">
          <SectionHeading icon={UserRound}>
            <span id="identity-title">Identité</span>
          </SectionHeading>
          <dl className="grid grid-cols-2 gap-x-5 gap-y-4">
            <DetailField label="NPI" value={npi} />
            <DetailField label="Date de naissance" value={birthdateLabel} />
            <DetailField label="Sexe" value={sexLabel} />
            <DetailField label="Département" value={department} />
            <DetailField label="N° de passeport" value={passport} />
            <DetailField label="Date d'expiration" value={passportExpirationLabel} />
            {isInternational && <DetailField label="NIC" value={nic} />}
            {isAGLO && <DetailField label="Société" value={agencyName} />}
          </dl>
        </section>

        {(isAgency || isAGLO) && (
          <section className="mt-5 border-b pb-6" aria-labelledby="registration-context-title">
            <h2 id="registration-context-title" className="mb-3 text-[13px] font-semibold text-[#52665a]">
              Contexte d’inscription
            </h2>
            <dl className="grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] items-start gap-x-6 rounded bg-[#f8faf9] px-3 py-2.5">
              <DetailField label="Auto-inscription" value={selfRegistered ? 'Oui' : 'Non'} />
              <DetailField
                label="Groupement"
                value={isGrouped && groupName?.length ? `Groupe ${groupName}` : 'Aucun groupement'}
              />
            </dl>
            {isAgency && selfRegistered && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-[#e0e9e3] bg-[#f4f8f5] px-2.5 py-2 text-xs leading-[19px] text-[#52675b]">
                <Info className="mt-0.5 size-[15px] shrink-0 text-[#698271]" aria-hidden="true" />
                <p>Ce pèlerin s’est inscrit lui-même. Les actions de la Société agréée ne sont pas disponibles.</p>
              </div>
            )}
          </section>
        )}

        <section className="mt-6" aria-labelledby="finance-title">
          <SectionHeading icon={Banknote}>
            <span id="finance-title">Finance</span>
          </SectionHeading>
          <dl className="grid grid-cols-3 divide-x overflow-hidden rounded-lg border bg-gray-50/70">
            {[
              ['Montant dû', amountDue],
              ['Payé', amountPaid],
              ['Reste', amountRemaining],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0 px-3 py-3">
                <dt className="text-[11px] text-gray-500">{label}</dt>
                <dd
                  className={`mt-1 break-words text-sm font-semibold tabular-nums ${
                    label === 'Payé' ? 'text-emerald-700' : 'text-gray-900'
                  }`}
                >
                  {formatMoney(value)}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Historique des paiements</h3>
              <span className="text-xs text-gray-500">
                {depositHistory.length} paiement{depositHistory.length === 1 ? '' : 's'}
              </span>
            </div>
            {depositHistory.length ? (
              <div className="space-y-2">
                {depositHistory.map((deposit, index) => (
                  <PilgrimDepositItem key={`${id || 'pilgrim'}-${deposit.id || index}`} deposit={deposit} />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-gray-500">
                Aucun dépôt enregistré.
              </p>
            )}
          </div>
        </section>

        {isAGLO && (
          <section className="mt-6 border-t border-[#e3e8e7] pt-[22px]" aria-label="Historique des visites médicales">
            <details key={id} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md text-sm font-semibold leading-[1.5] text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary [&::-webkit-details-marker]:hidden">
                <span>
                  Historique des visites médicales{' '}
                  {medicalHistory.length > 0 && (
                    <span className="font-normal text-[#728076]">({medicalHistory.length})</span>
                  )}
                </span>
                <ChevronDown
                  className="size-4 shrink-0 text-[#728076] transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="pt-3.5">
                {medicalHistory.length > 0 ? (
                  <MedicalVisitHistory visits={medicalHistory} />
                ) : (
                  <p className="pb-1.5 pt-0.5 text-[13px] text-[#78847c]">Aucune visite médicale enregistrée.</p>
                )}
              </div>
            </details>
          </section>
        )}
        {note && (
          <section className="mt-6 border-t pt-5" aria-labelledby="notes-title">
            <SectionHeading icon={StickyNote}>
              <span id="notes-title">Notes</span>
            </SectionHeading>
            <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">{note}</p>
          </section>
        )}
      </div>

      {(isAgency || isAGLO) && (
        <SheetFooter
          className={
            isAGLO
              ? 'shrink-0 flex-col gap-[9px] border-t bg-white px-[18px] py-3 sm:flex-row sm:px-6 sm:py-3.5'
              : 'shrink-0 flex-col border-t bg-white px-5 py-3 sm:flex-row'
          }
        >
          {isAgency && (
            <>
              {depositAllowed && (
                <SheetClose asChild>
                  <LockableButton
                    variant="hajj"
                    className="w-full min-w-0 sm:flex-1"
                    onClick={() => onOpenInvoiceModal(pilgrimData)}
                  >
                    <Coins /> Générer une facture
                  </LockableButton>
                </SheetClose>
              )}
              {!selfRegistered && (
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    className="w-full min-w-0 sm:flex-1"
                    onClick={() => onOpenEditModal(pilgrimData)}
                  >
                    <UserRoundPen /> Modifier
                  </Button>
                </SheetClose>
              )}
              {(!isNpiValid || selfRegistered) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="w-full min-w-0 sm:flex-1">
                      <LockableButton variant="hajj" className="w-full" disabled>
                        <Coins /> Générer une facture
                      </LockableButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {!isNpiValid && (
                      <p>Opération impossible : NPI non valide. Veuillez vérifier les informations fournies.</p>
                    )}
                    {selfRegistered && <p>Opération impossible : Vous n&apos;avez pas ajouté ce pèlerin.</p>}
                  </TooltipContent>
                </Tooltip>
              )}
              {selfRegistered && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="w-full min-w-0 sm:flex-1">
                      <LockableButton variant="outline" className="w-full" disabled>
                        <UserRoundPen /> Modifier
                      </LockableButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Opération impossible : Vous n&apos;avez pas ajouté ce pèlerin.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </>
          )}
          {isAGLO && (
            <>
              <SheetClose asChild>
                <Button
                  variant="outline"
                  className="h-[42px] w-full min-w-0 gap-2 rounded-[7px] border-[#cdd7d0] px-[13px] py-2.5 text-sm leading-5 [font-weight:550] hover:bg-[#f5f8f5] sm:flex-1 [&_svg]:size-[17px]"
                  disabled={isReadOnly}
                  onClick={() => onOpenEditModal(pilgrimData)}
                >
                  <UserRoundPen /> Modifier
                </Button>
              </SheetClose>
              <SheetClose asChild>
                <Button
                  variant="outline"
                  className="h-[42px] w-full min-w-0 rounded-[7px] border-[#cdd7d0] px-[13px] py-2.5 text-sm leading-5 text-[#aa3440] [font-weight:550] hover:bg-[#fff7f7] hover:text-[#aa3440] sm:flex-1"
                  disabled={isReadOnly}
                  onClick={() => onOpenDeleteModal(pilgrimData)}
                >
                  Supprimer
                </Button>
              </SheetClose>
            </>
          )}
        </SheetFooter>
      )}
    </SheetContent>
  );
};

PilgrimDetails.propTypes = {
  pilgrimData: PropTypes.shape({}).isRequired,
  isReadOnly: PropTypes.bool,
  onOpenEditModal: PropTypes.func,
  onOpenInvoiceModal: PropTypes.func,
  onOpenDeleteModal: PropTypes.func,
  handleGenerateMedicalCertificate: PropTypes.func,
  onOpenAutoFocus: PropTypes.func,
  onCloseAutoFocus: PropTypes.func,
};

export default PilgrimDetails;
